import { authFetch } from "../utils/fetchWithRefresh";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const scheduleBasedRequest = {
	// --- Schedule endpoints ---

	async getScheduleById(scheduleId) {
		const res = await authFetch(`${API_BASE}/schedules/${scheduleId}`, {
			method: "GET",
			headers: { "Content-Type": "application/json" },
		});

		if (!res.ok) throw new Error("Failed to fetch schedule");
		return res.json();
	},

	async createSchedule(scheduleData) {
		const res = await authFetch(`${API_BASE}/schedules`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(scheduleData),
		});

		if (!res.ok) throw new Error("Failed to create schedule");
		return res.json();
	},

	async createBooking(bookingData, token) {
		const res = await authFetch(`${API_BASE}/bookings`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				// ...(token ? { Authorization: `Bearer ${token}` } : {}),
			},
			body: JSON.stringify(bookingData),
		});

		/* The bare "Failed to create booking" hid every reason the server gives —
		   a missing field, a taken slot, hours that don't fit. Carry the body
		   through so the console says which one. */
		if (!res.ok) {
			const body = await res.json().catch(() => null);
			const err = new Error(body?.error || "Failed to create booking");
			err.status = res.status;
			err.fields = body?.fields;
			err.details = body?.details;
			throw err;
		}

		return res.json();
	},

	async updateSchedule(scheduleId, updates, token) {
		const res = await authFetch(`${API_BASE}/schedules/${scheduleId}`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`, // ✅ send Cognito AccessToken
			},
			body: JSON.stringify(updates),
		});

		if (!res.ok) {
			const err = await res.json().catch(() => ({}));
			throw new Error(err.error || "Failed to update schedule");
		}

		return res.json();
	},

	// --- Availability endpoints ---

	async getstaffAvailability(scheduleId, { date, duration, interval }) {
		const params = new URLSearchParams({ date, duration, interval });
		const res = await authFetch(`${API_BASE}/schedreaules/${scheduleId}/availability?${params}`, {
			method: "GET",
			headers: { "Content-Type": "application/json" },
		});

		if (!res.ok) throw new Error("Failed to fetch staff availability");
		return res.json();
	},

	async getAvailability(id, { startDate, duration, interval, autoAssign } = {}) {
		const params = new URLSearchParams();

		if (startDate) params.append("startDate", startDate);
		if (duration) params.append("duration", duration);
		if (interval) params.append("interval", interval);
		if (autoAssign) params.append("autoAssign", autoAssign);

		const res = await authFetch(`${API_BASE}/availability/${id}?${params.toString()}`, {
			method: "GET",
			headers: { "Content-Type": "application/json" },
		});

		if (!res.ok) throw new Error("Failed to fetch availability");
		return res.json();
	},

	async cancelBooking(bookingId, token) {
		const headers = { "Content-Type": "application/json" };
		if (token) headers.Authorization = `Bearer ${token}`; // only add if logged in

		const res = await authFetch(`${API_BASE}/bookings/${bookingId}/cancel`, {
			method: "DELETE",
			headers,
		});

		if (!res.ok) throw new Error("Failed to cancel booking");
		return res.json();
	},

	async rescheduleBooking(bookingId, data, token) {
		const headers = { "Content-Type": "application/json" };
		if (token) headers.Authorization = `Bearer ${token}`; // only add if logged in

		const res = await authFetch(`${API_BASE}/bookings/${bookingId}/reschedule`, {
			method: "PUT",
			headers,
			body: JSON.stringify(data),
		});

		if (!res.ok) throw new Error("Failed to reschedule booking");
		return res.json();
	},

	async addScheduleExclusion(scheduleId, exclusionData, token) {
		const res = await authFetch(`${API_BASE}/schedule/${scheduleId}/add/exclusions`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: token ? `Bearer ${token}` : "", // ✅ Attach token if available
			},
			body: JSON.stringify(exclusionData),
		});

		if (!res.ok) {
			const errorText = await res.text();
			throw new Error(`Failed to add exclusion: ${errorText}`);
		}

		return res.json(); // { message, exclusion, updated }
	},

	/* `target` is the exclusion itself, not a slot number. The endpoint used to
	   take an array position, but the week endpoints trim already-ended
	   exclusions before sending them, so our position is a position in a
	   shorter list — every expired entry shifted it by one and the wrong block
	   got deleted while the request still returned 200.

	   exclusionId is what the server prefers; `match` identifies entries stored
	   before ids existed; `index` is only still sent so an un-deployed server
	   behaves the way it always did. A plain number is accepted too, for any
	   caller that has not been updated. */
	async deleteScheduleExclusion(scheduleId, target, token) {
		const body =
			typeof target === "number"
				? { index: target }
				: {
						exclusionId: target?.exclusionId,
						match: {
							startDate: target?.startDate,
							endDate: target?.endDate,
							reason: target?.reason || "",
							blocks: target?.blocks || [],
						},
						index: target?.index,
				  };

		const res = await authFetch(`${API_BASE}/schedule/${scheduleId}/delete/exclusions`, {
			method: "DELETE",
			headers: {
				"Content-Type": "application/json",
				Authorization: token ? `Bearer ${token}` : "",
			},
			body: JSON.stringify(body),
		});

		if (!res.ok) {
			const errorText = await res.text();
			throw new Error(`Failed to delete exclusion: ${errorText}`);
		}

		return res.json(); // { message, updated }
	},
};

export default scheduleBasedRequest;
